<template>
  <div v-if="isOpen" class="rider-verify-nudge-overlay">
    <div class="rider-verify-nudge-card card shadow-lg">
      <div class="card-body p-4">
        <button type="button" class="btn-close float-end" aria-label="Dismiss" @click="dismiss"></button>
        <h3 class="fw-bold mb-1">Verify your contact info</h3>
        <p class="text-muted mb-4">We use this to keep you posted about your ride and account.</p>

        <div v-if="needsEmail" class="mb-4">
          <h6 class="fw-bold text-uppercase text-muted small mb-1"><i class="fa fa-envelope me-1"></i> Email</h6>
          <p v-if="localEmail && !localEmailVerified" class="text-muted small mb-2">This email hasn't been verified yet.</p>
          <div v-if="emailState.error" class="alert alert-danger py-2 mb-2">{{ emailState.error }}</div>
          <div v-if="emailState.info" class="alert alert-info py-2 mb-2">{{ emailState.info }}</div>

          <template v-if="emailState.hasExistingProfile">
            <p class="text-muted small">Linking will sign that account out on any other device it's currently signed into.</p>
            <button class="btn btn-primary w-100 mb-2" type="button" :disabled="emailState.loading" @click="linkProfile('email')">
              <span v-if="emailState.loading" class="spinner-border spinner-border-sm me-2"></span>
              {{ emailState.loading ? 'Linking...' : 'Link this profile' }}
            </button>
            <button class="btn btn-link p-0" type="button" :disabled="emailState.loading" @click="resetToVerification('email')">
              Use a different email
            </button>
          </template>
          <form v-else @submit.prevent="onSubmit('email')">
            <input class="form-control" type="email" v-model.trim="emailState.value"
                   :disabled="emailState.otpSent" placeholder="email@address.com"/>
            <div v-if="emailState.otpSent" class="mt-2">
              <input class="form-control" type="text" inputmode="numeric" v-model.trim="emailState.otpCode"
                     placeholder="Enter the code we sent you"/>
              <div class="text-end mt-1">
                <button type="button" class="btn btn-link p-0 btn-sm"
                        :disabled="emailState.resendSeconds > 0 || emailState.loading" @click="sendCode('email')">
                  {{ emailState.resendSeconds > 0 ? `Resend in ${emailState.resendSeconds}s` : 'Resend code' }}
                </button>
              </div>
            </div>
            <button class="btn btn-primary w-100 mt-2" type="submit" :disabled="emailState.loading">
              <span v-if="emailState.loading" class="spinner-border spinner-border-sm me-2"></span>
              {{ emailState.otpSent ? 'Verify' : 'Send code' }}
            </button>
          </form>
        </div>

        <div v-if="needsPhone">
          <h6 class="fw-bold text-uppercase text-muted small mb-1"><i class="fa fa-phone me-1"></i> Phone</h6>
          <p v-if="localPhoneNumber && !localPhoneNumberVerified" class="text-muted small mb-2">This phone number hasn't been verified yet.</p>
          <div v-if="phoneState.error" class="alert alert-danger py-2 mb-2">{{ phoneState.error }}</div>
          <div v-if="phoneState.info" class="alert alert-info py-2 mb-2">{{ phoneState.info }}</div>

          <template v-if="phoneState.hasExistingProfile">
            <p class="text-muted small">Linking will sign that account out on any other device it's currently signed into.</p>
            <button class="btn btn-primary w-100 mb-2" type="button" :disabled="phoneState.loading" @click="linkProfile('phone')">
              <span v-if="phoneState.loading" class="spinner-border spinner-border-sm me-2"></span>
              {{ phoneState.loading ? 'Linking...' : 'Link this profile' }}
            </button>
            <button class="btn btn-link p-0" type="button" :disabled="phoneState.loading" @click="resetToVerification('phone')">
              Use a different phone number
            </button>
          </template>
          <form v-else @submit.prevent="onSubmit('phone')">
            <div class="input-group">
              <select class="form-select" v-model="phoneState.countryCode" :disabled="phoneState.otpSent" style="max-width: 120px;">
                <option value="+1">+1 (US)</option>
                <option value="+1">+1 (CA)</option>
              </select>
              <input class="form-control" type="tel" v-model.trim="phoneState.value"
                     :disabled="phoneState.otpSent" placeholder="5551234567"/>
            </div>
            <div v-if="phoneState.otpSent" class="mt-2">
              <input class="form-control" type="text" inputmode="numeric" v-model.trim="phoneState.otpCode"
                     placeholder="Enter the code we sent you"/>
              <div class="text-end mt-1">
                <button type="button" class="btn btn-link p-0 btn-sm"
                        :disabled="phoneState.resendSeconds > 0 || phoneState.loading" @click="sendCode('phone')">
                  {{ phoneState.resendSeconds > 0 ? `Resend in ${phoneState.resendSeconds}s` : 'Resend code' }}
                </button>
              </div>
            </div>
            <button class="btn btn-primary w-100 mt-2" type="submit" :disabled="phoneState.loading">
              <span v-if="phoneState.loading" class="spinner-border spinner-border-sm me-2"></span>
              {{ phoneState.otpSent ? 'Verify' : 'Send code' }}
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onUnmounted } from 'vue';
import { RIDER_VERIFY_NUDGE_DISMISS_KEY } from '../utility/riderVerifyNudge.js';

const props = defineProps({
  email: { type: String, default: '' },
  phoneNumber: { type: String, default: '' },
  emailVerified: { type: Boolean, default: false },
  phoneNumberVerified: { type: Boolean, default: false },
});

// Local mirror of the rider's contact/verification state, updated in place on a successful
// verify instead of following redirectUrl -- resolving one channel shouldn't reload the page
// and abandon progress on the other channel's in-progress code entry.
const localEmail = ref(props.email);
const localPhoneNumber = ref(props.phoneNumber);
const localEmailVerified = ref(props.emailVerified);
const localPhoneNumberVerified = ref(props.phoneNumberVerified);

const needsEmail = computed(() => !localEmail.value || !localEmailVerified.value);
const needsPhone = computed(() => !localPhoneNumber.value || !localPhoneNumberVerified.value);

const dismissed = ref(sessionStorage.getItem(RIDER_VERIFY_NUDGE_DISMISS_KEY) === 'true');
// Closes automatically once both channels resolve -- needsEmail/needsPhone both go false, so
// isOpen just follows; no separate "mark complete" step needed.
const isOpen = computed(() => (needsEmail.value || needsPhone.value) && !dismissed.value);

function dismiss() {
  dismissed.value = true;
  sessionStorage.setItem(RIDER_VERIFY_NUDGE_DISMISS_KEY, 'true');
}

// The country-code dropdown only ever offers "+1" today (matches RiderCompleteProfileForm.vue) --
// splitting on that fixed 2-char prefix is enough to pre-fill an existing, unverified number.
function splitPhoneForEditing(fullNumber) {
  if (fullNumber && fullNumber.startsWith('+1')) {
    return { countryCode: '+1', digits: fullNumber.slice(2) };
  }
  return { countryCode: '+1', digits: fullNumber || '' };
}

function makeChannelState(initialValue, initialCountryCode = '+1') {
  return reactive({
    value: initialValue,
    countryCode: initialCountryCode,
    otpSent: false,
    otpCode: '',
    loading: false,
    error: null,
    info: null,
    resendSeconds: 0,
    hasExistingProfile: false,
  });
}

const initialPhone = splitPhoneForEditing(localPhoneNumber.value);
const emailState = makeChannelState(localEmail.value);
const phoneState = makeChannelState(initialPhone.digits, initialPhone.countryCode);

function stateFor(channel) {
  return channel === 'email' ? emailState : phoneState;
}

// The contact sent to the backend for a given channel: raw email, or country code + digits.
function identifierFor(channel) {
  return channel === 'email' ? emailState.value : `${phoneState.countryCode}${phoneState.value}`;
}

const resendTimers = { email: null, phone: null };

function startResendCountdown(channel) {
  stopResend(channel);
  const state = stateFor(channel);
  state.resendSeconds = 30;
  resendTimers[channel] = setInterval(() => {
    if (state.resendSeconds > 0) state.resendSeconds -= 1;
    else stopResend(channel);
  }, 1000);
}

function stopResend(channel) {
  if (resendTimers[channel]) {
    clearInterval(resendTimers[channel]);
    resendTimers[channel] = null;
  }
}

async function postJson(url, payload) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  let data = null;
  try {
    data = await res.json();
  } catch (e) { /* may be a plain error string */
  }
  if (!res.ok) {
    const text = data ? (data.message || '') : await res.text().catch(() => '');
    throw new Error(text || 'Something went wrong. Please try again.');
  }
  return data || {};
}

async function sendCode(channel) {
  const state = stateFor(channel);
  state.error = null;
  state.info = null;
  if (!state.value) {
    state.error = channel === 'email' ? 'Please enter your email.' : 'Please enter your phone number.';
    return;
  }
  state.loading = true;
  try {
    const data = await postJson('/api/portal/rider/send-signup-otp', {
      userIdentifier: identifierFor(channel),
      isEmail: channel === 'email',
    });
    if (data.success === false) {
      state.error = data.message || 'Unable to send verification code.';
      return;
    }
    state.otpSent = true;
    state.info = data.message || 'Verification code sent.';
    startResendCountdown(channel);
  } catch (e) {
    state.error = e.message;
  } finally {
    state.loading = false;
  }
}

function onSubmit(channel) {
  return stateFor(channel).otpSent ? verifyCode(channel) : sendCode(channel);
}

async function verifyCode(channel) {
  const state = stateFor(channel);
  state.error = null;
  if (!state.otpCode) {
    state.error = 'Please enter the verification code.';
    return;
  }
  state.loading = true;
  try {
    // The inactive channel's field and a referral code are deliberately omitted:
    // completeRiderProfile's otpIsEmail branch never reads the other field, and every rider who
    // can see this dialog already has profileComplete=true, so a referral code here would 400.
    const data = await postJson('/api/portal/rider/complete-profile', {
      email: channel === 'email' ? state.value : null,
      phoneNumber: channel === 'phone' ? identifierFor('phone') : null,
      otpCode: state.otpCode,
      otpIsEmail: channel === 'email',
    });
    if (data.success && data.redirectUrl) {
      if (channel === 'email') {
        localEmail.value = state.value;
        localEmailVerified.value = true;
      } else {
        localPhoneNumber.value = identifierFor('phone');
        localPhoneNumberVerified.value = true;
      }
      stopResend(channel);
    } else if (data.hasExistingProfile) {
      state.hasExistingProfile = true;
      state.error = data.message || 'This contact is already linked to another account.';
    } else {
      state.error = data.message || 'Unable to verify this contact.';
    }
  } catch (e) {
    state.error = e.message;
  } finally {
    state.loading = false;
  }
}

async function linkProfile(channel) {
  const state = stateFor(channel);
  state.loading = true;
  state.error = null;
  try {
    const data = await postJson('/api/portal/rider/link-profile', {
      email: channel === 'email' ? state.value : null,
      phoneNumber: channel === 'phone' ? identifierFor('phone') : null,
      otpIsEmail: channel === 'email',
    });
    if (data.success && data.redirectUrl) {
      // Linking merges into a *different* rider and mints a new session for it -- unlike a plain
      // verify, this page's own rider identity is now stale, so (matching
      // RiderCompleteProfileForm.vue's own linkProfile()) this path still navigates away.
      window.location.href = data.redirectUrl;
    } else {
      resetToVerification(channel, data.message || 'Unable to link this profile. Please verify again.');
    }
  } catch (e) {
    resetToVerification(channel, e.message);
  } finally {
    state.loading = false;
  }
}

function resetToVerification(channel, message = null) {
  const state = stateFor(channel);
  state.error = message;
  state.hasExistingProfile = false;
  state.otpSent = false;
  state.otpCode = '';
}

onUnmounted(() => {
  stopResend('email');
  stopResend('phone');
});
</script>

<style scoped>
.rider-verify-nudge-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.rider-verify-nudge-card {
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  border: none;
  border-radius: 15px;
}
</style>
